import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../../services/authService";
import "../../styles/AdminCompatibilities.css";

export default function AdminCompatibilities() {
  const { t } = useTranslation();

  const [brands, setBrands] = useState([]);
  const [types, setTypes] = useState([]);
  const [models, setModels] = useState([]);
  const [products, setProducts] = useState([]);

  const [brandName, setBrandName] = useState("");
  const [typeName, setTypeName] = useState("");

  const [modelForm, setModelForm] = useState({
    name: "",
    brandId: "",
    equipmentTypeId: "",
  });

  const [compatibilityForm, setCompatibilityForm] = useState({
    productId: "",
    equipmentModelId: "",
  });

  const [message, setMessage] = useState("");

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    await Promise.all([
      loadBrands(),
      loadTypes(),
      loadModels(),
      loadProducts(),
    ]);
  };

  const loadBrands = async () => {
    const response = await fetch("/api/admin/compatibilities/brands", {
      credentials: "include",
    });

    if (response.ok) {
      setBrands(await response.json());
    }
  };

  const loadTypes = async () => {
    const response = await fetch("/api/admin/compatibilities/equipment-types", {
      credentials: "include",
    });

    if (response.ok) {
      setTypes(await response.json());
    }
  };

  const loadModels = async () => {
    const response = await fetch("/api/admin/compatibilities/equipment-models", {
      credentials: "include",
    });

    if (response.ok) {
      setModels(await response.json());
    }
  };

  const loadProducts = async () => {
    const response = await fetch("/api/products", {
      credentials: "include",
    });

    if (response.ok) {
      setProducts(await response.json());
    }
  };

  const createBrand = async (e) => {
    e.preventDefault();

    const csrf = await getCsrfToken();

    const response = await fetch("/api/admin/compatibilities/brands", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [csrf.headerName]: csrf.token,
      },
      body: JSON.stringify({ name: brandName }),
    });

    if (response.ok) {
      setBrandName("");
      setMessage(t("adminCompatibilities.messages.brandCreated"));
      loadBrands();
    } else {
      setMessage(t("adminCompatibilities.messages.brandError"));
    }
  };

  const createType = async (e) => {
    e.preventDefault();

    const csrf = await getCsrfToken();

    const response = await fetch("/api/admin/compatibilities/equipment-types", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [csrf.headerName]: csrf.token,
      },
      body: JSON.stringify({ name: typeName }),
    });

    if (response.ok) {
      setTypeName("");
      setMessage(t("adminCompatibilities.messages.typeCreated"));
      loadTypes();
    } else {
      setMessage(t("adminCompatibilities.messages.typeError"));
    }
  };

  const createModel = async (e) => {
    e.preventDefault();

    const csrf = await getCsrfToken();

    const response = await fetch("/api/admin/compatibilities/equipment-models", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [csrf.headerName]: csrf.token,
      },
      body: JSON.stringify({
        name: modelForm.name,
        brandId: Number(modelForm.brandId),
        equipmentTypeId: Number(modelForm.equipmentTypeId),
      }),
    });

    if (response.ok) {
      setModelForm({
        name: "",
        brandId: "",
        equipmentTypeId: "",
      });
      setMessage(t("adminCompatibilities.messages.modelCreated"));
      loadModels();
    } else {
      setMessage(t("adminCompatibilities.messages.modelError"));
    }
  };

  const addCompatibility = async (e) => {
    e.preventDefault();

    const csrf = await getCsrfToken();

    const response = await fetch("/api/admin/compatibilities", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [csrf.headerName]: csrf.token,
      },
      body: JSON.stringify({
        productId: Number(compatibilityForm.productId),
        equipmentModelId: Number(compatibilityForm.equipmentModelId),
      }),
    });

    if (response.ok) {
      setCompatibilityForm({
        productId: "",
        equipmentModelId: "",
      });
      setMessage(t("adminCompatibilities.messages.compatibilityCreated"));
    } else {
      setMessage(t("adminCompatibilities.messages.compatibilityError"));
    }
  };

  const sortedModels = useMemo(() => {
    return [...models].sort((a, b) =>
      `${a.brandName} ${a.name}`.localeCompare(`${b.brandName} ${b.name}`)
    );
  }, [models]);

  return (
    <main className="admin-page">
      <section className="admin-container">
        <div className="admin-compatibilities">
          <div className="admin-compatibilities-header">
            <h1>{t("adminCompatibilities.title")}</h1>
            <p>{t("adminCompatibilities.subtitle")}</p>
          </div>

          {message && (
            <div className="admin-compatibilities-message">
              {message}
            </div>
          )}

          <section className="admin-compatibilities-grid">
            <form className="compatibility-card" onSubmit={createBrand}>
              <h2>{t("adminCompatibilities.createBrand.title")}</h2>

              <input
                type="text"
                placeholder={t("adminCompatibilities.createBrand.placeholder")}
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                required
              />

              <button type="submit">
                {t("adminCompatibilities.createBrand.button")}
              </button>
            </form>

            <form className="compatibility-card" onSubmit={createType}>
              <h2>{t("adminCompatibilities.createType.title")}</h2>

              <input
                type="text"
                placeholder={t("adminCompatibilities.createType.placeholder")}
                value={typeName}
                onChange={(e) => setTypeName(e.target.value)}
                required
              />

              <button type="submit">
                {t("adminCompatibilities.createType.button")}
              </button>
            </form>

            <form className="compatibility-card" onSubmit={createModel}>
              <h2>{t("adminCompatibilities.createModel.title")}</h2>

              <input
                type="text"
                placeholder={t("adminCompatibilities.createModel.placeholder")}
                value={modelForm.name}
                onChange={(e) =>
                  setModelForm((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                required
              />

              <select
                value={modelForm.brandId}
                onChange={(e) =>
                  setModelForm((prev) => ({
                    ...prev,
                    brandId: e.target.value,
                  }))
                }
                required
              >
                <option value="">
                  {t("adminCompatibilities.createModel.selectBrand")}
                </option>

                {brands.map((brand) => (
                  <option value={brand.id} key={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>

              <select
                value={modelForm.equipmentTypeId}
                onChange={(e) =>
                  setModelForm((prev) => ({
                    ...prev,
                    equipmentTypeId: e.target.value,
                  }))
                }
                required
              >
                <option value="">
                  {t("adminCompatibilities.createModel.selectType")}
                </option>

                {types.map((type) => (
                  <option value={type.id} key={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>

              <button type="submit">
                {t("adminCompatibilities.createModel.button")}
              </button>
            </form>

            <form className="compatibility-card" onSubmit={addCompatibility}>
              <h2>{t("adminCompatibilities.addCompatibility.title")}</h2>

              <select
                value={compatibilityForm.productId}
                onChange={(e) =>
                  setCompatibilityForm((prev) => ({
                    ...prev,
                    productId: e.target.value,
                  }))
                }
                required
              >
                <option value="">
                  {t("adminCompatibilities.addCompatibility.selectProduct")}
                </option>

                {products.map((product) => (
                  <option value={product.id} key={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>

              <select
                value={compatibilityForm.equipmentModelId}
                onChange={(e) =>
                  setCompatibilityForm((prev) => ({
                    ...prev,
                    equipmentModelId: e.target.value,
                  }))
                }
                required
              >
                <option value="">
                  {t("adminCompatibilities.addCompatibility.selectModel")}
                </option>

                {sortedModels.map((model) => (
                  <option value={model.id} key={model.id}>
                    {model.brandName} {model.name} ·{" "}
                    {model.equipmentTypeName}
                  </option>
                ))}
              </select>

              <button type="submit">
                {t("adminCompatibilities.addCompatibility.button")}
              </button>
            </form>
          </section>

          <section className="compatibility-list-section">
            <h2>{t("adminCompatibilities.modelsList.title")}</h2>

            <div className="compatibility-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>{t("adminCompatibilities.modelsList.brand")}</th>
                    <th>{t("adminCompatibilities.modelsList.model")}</th>
                    <th>{t("adminCompatibilities.modelsList.type")}</th>
                  </tr>
                </thead>

                <tbody>
                  {sortedModels.map((model) => (
                    <tr key={model.id}>
                      <td>{model.brandName}</td>
                      <td>{model.name}</td>
                      <td>{model.equipmentTypeName}</td>
                    </tr>
                  ))}

                  {sortedModels.length === 0 && (
                    <tr>
                      <td colSpan="3">
                        {t("adminCompatibilities.modelsList.empty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}